import React, { useRef } from 'react';

// Components

import AlertInfo from '@components/common/alert/AlertInfo';
import InstructionalText from '@components/ssap/notifications/InstructionalText';
import Tooltip from '@components/common/tooltip/Tooltip';

// Utils

import { normalizeStringToBooleanValue } from '@utils/normalize/normalizeStringToBooleanValue';

const RadioButtons = props => {
    const {
        additionalContent = null,
        inputData: {
            disabled: disabledFromComponent,
            id,
            inputClass,
            liClass,
            managingValues = {},
            options,
            optionsReplacements,
            returnAsNumber,
            type,
            value
        },
        label: { fieldSetClass, labelClass, ulClass, showRequiredIcon, isLabelVisible = true },
        legend: {
            dataInstructions,
            dataInstructionsAnchor: anchor,
            dataInstructionsRightSide,
            replacements,
            legendText,
            instructionsViaPortal: [isPortal, portalMargin] = [],
            onlyInstructions,
        },
    } = props;
    
    const handleValueChange = e => {
        const {
            dataset: { value },
        } = e.currentTarget;
        props.inputActions.handleFieldChange(normalizeStringToBooleanValue(value, returnAsNumber));
    };

    const supportNullValueForOption = (option = {}) => {
        return option.value === null ? 'null' : option.value;
    };

    const wrapWithComponent = (componentName, textId, key) => {
        const supportedComponents = {
            'AlertInfo': {
                Component: AlertInfo,
                key,
                id: textId,
                textReplacements: optionsReplacements,
                skipIntl: true
            }
        };

        const {
            Component,
            ...restOfPropsForWrapper
        } = supportedComponents[componentName];

        return (
            <Component { ...restOfPropsForWrapper } />
        );
    };

    // If it is needed multi-location react portal anchor may be implemented.
    // For a while there are only 2 usages for it (here and in Checkbox)
    const reactPortalAnchor = useRef(null);

    const legendDataTestId = `${id}_legend`;
    const legendTextId = `${id}_legend_text`;

    // A "Learn more" link in the <legend> takes the group's entry announcement, leaving the
    // radios unnamed. Naming the options container restates the question on entry.

    const isQuestionRestatedForOptions = isLabelVisible && !!dataInstructions;

    const optionsListElement = (
        <div
            className={`gi-radio-group ${ulClass ? ulClass : ''}`}
            {...(isQuestionRestatedForOptions
                ? { role: 'group', 'aria-labelledby': legendTextId }
                : {})}
        >
            {options.map((option, i) => {
                const {
                    asComponent,
                    isInputDisabled,
                    isInputShown,
                    isOnlyLabelShown,
                    optionValue
                } = typeof option.value === 'function'
                    ? option.value(managingValues)
                    : {
                        asComponent: null,
                        isInputDisabled: option.disabled,
                        isInputShown: true,
                        isOnlyLabelShown: false,
                        optionValue: option.value,
                        'data-testid': `${option.value}_label`
                    };

                const key = `${ option.optionId }_${ i }}`;

                if (isOnlyLabelShown) {
                    if(asComponent) {
                        return wrapWithComponent(asComponent, option.text, key);
                    }

                    return (
                        <div
                            key = { key }
                            className = 'gi-radio-group__label-only'
                        >
                            { option.text }
                        </div>
                    );
                }

                const optionIsChecked = optionValue === value;
                const disabled = disabledFromComponent || isInputDisabled;

                if (isInputShown) {
                    const inputId = `${id}_option${i}`;
                    const optionLabelId = `${id}_label${i}`;
                    // Create consistent option ID pattern: baseId_optionId
                    const optionTestId = `${id}_${option.optionId}`;
                    return (
                        <div key={`${option.text}_${i}`} className={`gi-radio-container ${liClass ? liClass.join(' ') : ''} ${option.tooltip ? 'display-flex flex-align-baseline lead' : ''}`}>
                            <input
                                id={inputId}
                                name={id}
                                className={`usa-radio__input gi-radio_buttons__input ${
                                    inputClass && inputClass.join(' ')
                                }`}
                                type="radio"
                                checked={optionIsChecked}
                                data-value={supportNullValueForOption({
                                    ...option,
                                    value: optionValue,
                                })}
                                data-testid={`${optionTestId}_input`}
                                onChange={handleValueChange}
                                disabled={disabled || false}
                            />
                            <label
                                className="usa-radio__label gi-radio_buttons__label"
                                id={optionLabelId}
                                data-testid={`${optionTestId}_label`}
                                htmlFor={inputId}
                            >
                                {option.text}
                            </label>
                            {option.tooltip && (
                                <Tooltip
                                    icon = 'info-circle'
                                    index={`${id}_option${i}`}
                                    triggerDecorationClassName = ''
                                    triggerClassName = 'margin-l-5'
                                    {...option.tooltip}
                                />
                            )}
                        </div>
                    );
                }
            })}
            {dataInstructionsRightSide && (
                <div>
                    <InstructionalText
                        anchor={anchor}
                        expandClassName="margin-t-10"
                        Component={<span data-instructions={dataInstructionsRightSide} />}
                    />
                </div>
            )}
        </div>
    );

    const portalAnchorElement = (
        <div ref={reactPortalAnchor} style={{ marginTop: portalMargin }} />
    );

    if (isLabelVisible) {
        return (
            <div className={`grid-row gi-component gi-${type}`}>
                <fieldset className={`usa-fieldset ${fieldSetClass}`} aria-labelledby={legendTextId}>
                    <legend data-testid={legendDataTestId} className={`${labelClass && labelClass.join(' ')}`}>
                        <span id={legendTextId} className={`required-${showRequiredIcon}`} data-testid={`${legendDataTestId}_span`}>{legendText}</span>
                        {dataInstructions && (
                            <InstructionalText
                                anchor={anchor}
                                Component={<span data-instructions={dataInstructions} />}
                                onlyInstructions={onlyInstructions}
                                replacements={replacements}
                                reactPortalAnchor={isPortal ? reactPortalAnchor : { current: null }}
                            />
                        )}
                    </legend>
                    {additionalContent}
                    {optionsListElement}
                    {portalAnchorElement}
                </fieldset>
            </div>
        );
    }

    return (
        <div className={`grid-row gi-component gi-${type}`}>
            {additionalContent}
            {optionsListElement}
            {portalAnchorElement}
        </div>
    );
};

export default RadioButtons;
